import { useState, useEffect, useCallback, useRef } from "react";

import Modal from "@acadia-ui/modal";
import Button, { BUTTON_DISPLAY, BUTTON_SIZE } from "@acadia-ui/button";
import Toggle from "@acadia-ui/toggle";

import "./StickyColumnControls.scss";

const useStickyColumnControlsModal = ({ hierarchyLevel, columns, setStickyState, setRefreshTable }) => {
	const [open, setOpen] = useState(false);
	const Trigger = () => {
		return (
			<Button
				size={BUTTON_SIZE.SMALL}
				display={BUTTON_DISPLAY.ICON_BORDERED}
				icon="icon-pin"
				onClick={() => Modal.show(`${hierarchyLevel}__stickyColumnControlsModal`)}
				style={{ order: 4 }}
				data-tracking-id-alt="edit-sticky-columns"
			>
				Edit Sticky Columns
			</Button>
		);
	};

	const Component = useCallback(
		({ stickyState }) => (
			<Modal
				id={`${hierarchyLevel}__stickyColumnControlsModal`}
				heading="Edit Sticky Columns"
				description="select which columns should remain visible when scrolling horizontally."
				// icon="icon-pin"
				onRequestClose={() => setOpen(false)}
			>
				{columns.map((col) => {
					const isSticky = stickyState?.[col?.props?.bind] || false;
					return (
						<div key={col?.props?.bind} className={`sticky-toggle__container`}>
							<Toggle
								id={`${hierarchyLevel}__stickyToggle__${col?.props?.bind}`}
								checked={isSticky}
								onChange={(e, val) => {
									setStickyState((prev) => ({
										...prev,
										[col?.props?.bind]: val
									}));
									setRefreshTable(true);
								}}
							>
								{col?.props?.columnSettings?.label || col?.props?.["data-label"] || col?.props?.children || col?.props?.bind}
							</Toggle>
						</div>
					);
				})}
			</Modal>
		),
		[open, columns.length]
	);

	return [Trigger, Component];
};

export default function useStickyColumnControls({ hierarchyLevel, setIsLoading, columns, dataTableApi }) {
	const [stickyState, setStickyState] = useState({});
	const [refreshTable, setRefreshTable] = useState(false);
	const hasTriggeredStickyPositionUpdate = useRef(false);

	// this code is highly hacky, and causes the DT's sticky scroll mutation observer to trigger and recalculate sticky positions
	// ideally we would trigger a recalculation directly, but that is not currently possible
	// so we append an empty row to the middle tbody to trigger the observer
	// this is only needed when there's a lot of data (2000+ rows) because the timing of the builtin MO fired too early (data isn't loaded yet)
	// useEffect(() => {
	// 	setTimeout(() => {
	// 		const above = document.querySelector(`#${hierarchyLevel}__table > tbody.gos-dt-tbody.gos-dt-tbody-above`);
	// 		const middle = document.querySelector(`#${hierarchyLevel}__table > tbody.gos-dt-tbody.gos-dt-tbody-middle`);
	// 		if (above?.children?.length > 0 && middle?.children?.length === 0) {
	// 			middle.appendChild(document.createElement("tr"));
	// 		}
	// 	}, 500);
	// }, [columns]);

	// this function is basically 1:1 copy pastedfrom useStickScroll.js hook in datatable component.
	// it can be removed & rteplaced with native call to `dataTableApi?.current?.updateStickyScrollPositions` in DataTable v2.1.2
	const updateStickyScrollPositions = useCallback(({ dataTableApi, bypass = false }) => {
		if (hasTriggeredStickyPositionUpdate.current) {
			return;
		}

		requestAnimationFrame(() => {
			const table = dataTableApi?.current?.element?.current;
			if (!table) return;

			// Get header row to calculate column widths once (all rows share same column structure)
			const headerRow = table.querySelector(`thead tr:not(.gos-dt-tr__accordion)`);
			if (!headerRow) return;

			const headerCells = Array.from(headerRow.children);
			const numColumns = headerCells.length;

			// Identify hidden columns to skip
			const hiddenColumnIndices = new Set();
			headerCells.forEach((cell, index) => {
				if (cell.className.includes("gos-dt__hide-column")) {
					hiddenColumnIndices.add(index);
				}
			});

			// BATCH READ: Calculate cumulative offsets from header row only
			// Use getBoundingClientRect which is faster than getComputedStyle
			const cumulativeOffsets = [];
			let currentOffset = 0;

			for (let i = 0; i < numColumns; i++) {
				// Store the offset before this column
				cumulativeOffsets.push(currentOffset);

				// Skip hidden columns - they don't contribute to offset
				if (hiddenColumnIndices.has(i)) {
					continue;
				}

				// Skip last column - it doesn't contribute to offset for other columns
				if (i === numColumns - 1) {
					continue;
				}

				// Add this cell's width to the running offset
				const cellWidth = headerCells[i].getBoundingClientRect().width || 0;
				currentOffset += cellWidth;
			}

			// Get all rows
			const allRows = table.querySelectorAll(`tr:not(.gos-dt-tr__accordion)`) || [];

			// BATCH WRITE: Apply offsets and classes to all rows
			allRows.forEach((row) => {
				const cells = row.children;
				let rowLastStickyCell = null;

				for (let i = 0; i < cells.length; i++) {
					const cell = cells[i];

					// Skip hidden columns
					if (hiddenColumnIndices.has(i)) {
						continue;
					}

					// For last cell, apply left shadow variant
					if (i === cells.length - 1) {
						cell.classList.add("sticky-shadow-left");
						continue;
					}

					// Apply left offset to sticky cells only (skip non-sticky)
					const isSticky = cell.classList.contains("gos-dt-td__sticky") || cell.classList.contains("gos-dt-th__sticky");
					if (isSticky) {
						cell.style.left = `${cumulativeOffsets[i]}px`;
						rowLastStickyCell = cell;
					}
				}

				// Add shadow to last sticky cell in this row
				if (rowLastStickyCell) {
					rowLastStickyCell.classList.add("sticky-shadow");
				}
			});

			if (!bypass) {
				hasTriggeredStickyPositionUpdate.current = true;
			}
		});
	}, []);

	useEffect(() => {
		setTimeout(() => {
			setRefreshTable(true);
		}, 1);
	}, []);

	// just in case, basically.
	useEffect(() => {
		setTimeout(() => {
			updateStickyScrollPositions({ dataTableApi, bypass: true });
		}, 500);
	}, [dataTableApi]);

	useEffect(() => {
		if (refreshTable) {
			setRefreshTable(false);
			hasTriggeredStickyPositionUpdate.current = false;
			setTimeout(() => {
				updateStickyScrollPositions({ dataTableApi, bypass: true });
			}, 500);
		}
	}, [refreshTable]);

	// check the initial state of columns and populate stickystate
	// Build sticky state from unique binds so duplicate columns cannot cause a reset loop.
	useEffect(() => {
		const nextStickyState = {};

		columns.forEach((col) => {
			const bind = col?.props?.bind;
			if (!bind || bind in nextStickyState) {
				return;
			}

			nextStickyState[bind] = stickyState?.[bind] ?? col?.props?.columnSettings?.defaultSticky ?? false;
		});

		const currentKeys = Object.keys(stickyState || {});
		const nextKeys = Object.keys(nextStickyState);
		const hasChanged = currentKeys.length !== nextKeys.length || nextKeys.some((key) => stickyState?.[key] !== nextStickyState[key]);

		if (hasChanged) {
			setStickyState(nextStickyState);
		}
	}, [columns, stickyState]);

	const [Trigger, Component] = useStickyColumnControlsModal({ hierarchyLevel, columns, stickyState, setStickyState, setIsLoading, setRefreshTable });
	return { StickyColumnControlsTrigger: Trigger, StickyColumnControlsModal: Component, stickyState, refreshTable, updateStickyScrollPositions };
}
